import type { ReactNode } from 'react';
import { Icon, type IconName } from '@capsico/ui';

/*
 * W18 growth blocks (capsico-customer-app-ui): refer-a-friend hero, stat tiles, invite rows,
 * rules list, Campus Pass choice rows + status card, cashback lines and the win-back card.
 * Presentation only — strings and amounts arrive formatted from the pages.
 */

/** Gradient hero (ink on yellow): headline, body, the code in a dashed chip, copy + share. */
export function ReferralHero({
  title,
  body,
  codeLabel,
  code,
  copyLabel,
  onCopy,
  shareLabel,
  onShare,
}: {
  title: string;
  body: string;
  codeLabel: string;
  code: string;
  copyLabel: string;
  onCopy: () => void;
  shareLabel: string;
  onShare: () => void;
}) {
  return (
    <section className="relative overflow-hidden rounded-sheet bg-linear-to-br from-brand-pale to-brand p-4 text-on-brand">
      <span
        aria-hidden
        className="absolute -top-10 -right-10 size-32 rounded-full bg-surface opacity-20"
      />
      <div className="relative">
        <span className="inline-flex size-11 items-center justify-center rounded-full bg-surface text-brand-ink">
          <Icon name="gift" size={22} />
        </span>
        <h2 className="mt-3 text-xl leading-7 font-bold">{title}</h2>
        <p className="mt-1 text-xs leading-5">{body}</p>
        <p className="mt-4 text-micro font-semibold tracking-wide uppercase">{codeLabel}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="flex h-11 min-w-0 flex-1 items-center rounded-field border-2 border-dashed border-on-brand/40 bg-surface/70 px-3 font-mono text-lg font-bold tracking-widest">
            <span className="truncate">{code}</span>
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-field bg-surface px-3 text-xs font-semibold text-ink"
          >
            <Icon name="copy" size={16} />
            {copyLabel}
          </button>
        </div>
        <button
          type="button"
          onClick={onShare}
          className="mt-3 inline-flex h-12 w-full items-center justify-center gap-2 rounded-field bg-ink text-sm font-semibold text-surface active:scale-[0.98]"
        >
          <Icon name="share" size={18} />
          {shareLabel}
        </button>
      </div>
    </section>
  );
}

/** Row of 3 number tiles (friends joined · waiting · earned). */
export function StatTiles({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="rounded-sheet bg-surface px-3 py-3 text-center shadow-[inset_0_0_0_1px_var(--color-hair)]"
        >
          <p className="text-lg font-bold text-ink tabular">{item.value}</p>
          <p className="mt-0.5 truncate text-caption text-ink-3">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

type Tone = 'positive' | 'negative' | 'neutral' | 'brand';
const CHIP: Record<Tone, string> = {
  positive: 'bg-positive-bg text-positive',
  negative: 'bg-negative-bg text-negative',
  neutral: 'bg-tile text-ink-2',
  brand: 'bg-brand-soft text-ink',
};

export function StatusChip({ tone, children }: { tone: Tone; children: string }) {
  return (
    <span
      className={`inline-flex h-5.5 shrink-0 items-center rounded-full px-2 text-micro font-semibold ${CHIP[tone]}`}
    >
      {children}
    </span>
  );
}

/** One invited friend: name, status chip, reward and date. */
export function InviteRow({
  friend,
  status,
  tone,
  detail,
  reward,
}: {
  friend: string;
  status: string;
  tone: Tone;
  detail: string;
  reward: string | null;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-bold text-brand-ink">
        {friend.slice(0, 1).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{friend}</p>
        <p className="truncate text-caption text-ink-3">{detail}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <StatusChip tone={tone}>{status}</StatusChip>
        {reward && <span className="text-xs font-semibold text-positive tabular">{reward}</span>}
      </div>
    </div>
  );
}

/** Numbered "how it works" list. */
export function RulesList({ title, rules }: { title: string; rules: string[] }) {
  return (
    <section className="rounded-sheet bg-surface p-4 shadow-[inset_0_0_0_1px_var(--color-hair)]">
      <h2 className="text-base font-bold text-ink">{title}</h2>
      <ol className="mt-3 space-y-3">
        {rules.map((rule, index) => (
          <li key={rule} className="flex gap-3">
            <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-on-brand">
              {index + 1}
            </span>
            <span className="text-xs leading-5 text-ink-2">{rule}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Radio row inside a hairline card (campus, verification method). */
export function ChoiceRow({
  icon,
  title,
  sub,
  picked,
  onPick,
}: {
  icon: IconName;
  title: string;
  sub?: string | null;
  picked: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={picked}
      onClick={onPick}
      className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-tile"
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand-ink">
        <Icon name={icon} size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink">{title}</span>
        {sub && <span className="block truncate text-caption text-ink-3">{sub}</span>}
      </span>
      <span
        className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full ${picked ? 'bg-brand shadow-[inset_0_0_0_1.5px_var(--color-brand-600)]' : 'shadow-[inset_0_0_0_1.5px_var(--color-control)]'}`}
      >
        {picked && <span className="size-2 rounded-full bg-on-brand" />}
      </span>
    </button>
  );
}

/** Campus Pass status card: coloured band, icon, status title, body lines and an optional action. */
export function PassStatusCard({
  tone,
  icon,
  label,
  title,
  lines,
  action,
}: {
  tone: 'positive' | 'warning' | 'negative' | 'neutral';
  icon: IconName;
  label: string;
  title: string;
  lines: string[];
  action?: ReactNode;
}) {
  const band = {
    positive: 'bg-linear-to-br from-brand-pale to-brand text-on-brand',
    warning: 'bg-warning-bg text-ink',
    negative: 'bg-negative-bg text-ink',
    neutral: 'bg-tile text-ink',
  }[tone];
  return (
    <section className={`rounded-sheet p-4 ${band}`}>
      <div className="flex items-center gap-3">
        <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-surface text-brand-ink">
          <Icon name={icon} size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-micro font-semibold tracking-wide uppercase opacity-80">{label}</p>
          <p className="truncate text-base font-bold">{title}</p>
        </div>
      </div>
      {lines.length > 0 && (
        <div className="mt-3 space-y-1">
          {lines.map((line) => (
            <p key={line} className="text-xs leading-5">
              {line}
            </p>
          ))}
        </div>
      )}
      {action && <div className="mt-4">{action}</div>}
    </section>
  );
}

/** One cashback line: order, expiry, amount (green). */
export function CashbackLine({
  title,
  sub,
  amount,
}: {
  title: string;
  sub: string;
  amount: string;
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-positive-bg text-positive">
        <Icon name="tag" size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{title}</p>
        <p className="truncate text-caption text-ink-3 tabular">{sub}</p>
      </div>
      <span className="shrink-0 text-sm font-semibold text-positive tabular">{amount}</span>
    </div>
  );
}

/** Green-tint banner with a tag icon (cashback hint on checkout, cashback earned on the order). */
export function CashbackBanner({ title, body }: { title: string; body?: string | null }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-positive-bg px-4 py-3">
      <span className="mt-0.5 text-positive">
        <Icon name="tag" size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {body && <p className="mt-0.5 text-xs text-ink-2">{body}</p>}
      </div>
    </div>
  );
}

/** Win-back card in the wallet: gift tile, headline, the credit and its expiry. */
export function WinbackCard({ title, body }: { title: string; body: string }) {
  return (
    <section className="flex items-center gap-3 rounded-sheet bg-brand-soft p-4 shadow-[inset_0_0_0_1px_var(--color-brand-600)]">
      <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-brand text-on-brand">
        <Icon name="gift" size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-ink">{title}</p>
        <p className="mt-0.5 text-xs text-ink-2">{body}</p>
      </div>
    </section>
  );
}
